import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  adminFinanceStatsQuery,
  adminPaymentsBetweenQuery,
  adminUserStatisticsQuery,
} from '../../queries/appQueries';
import {
  Text,
  Title,
  Group,
  Stack,
  useMantineColorScheme,
  Box,
  Card,
  SimpleGrid,
  Table,
  Button,
  Pagination,
  LoadingOverlay,
  Select,
  Modal,
  Code,
  Divider,
  RingProgress,
  ScrollArea,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { BarChart } from '@mantine/charts';
import PageTransition from '../../components/animations/PageTransition';
import CountUpNumber from '../../components/animations/CountUpNumber';
import AdminPageHeader from '../../components/AdminPageHeader';
import {
  IconCash,
  IconCreditCard,
  IconUsers,
  IconCalendar,
  IconSearch,
  IconTrendingUp,
  IconTrendingDown,
  IconDownload,
  IconReportMoney,
} from '@tabler/icons-react';
import {
  paymentHistoryService,
  PaymentHistoryDTO,
  FinanceStats,
} from '../../services/paymentHistoryService';
import { UserStatistics } from '../../services/userManagementService';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'COMPLETED', label: 'Completados' },
  { value: 'REFUNDED', label: 'Reembolsados' },
];

const PLAN_RING_COLORS = ['blue', 'teal', 'orange', 'grape', 'cyan', 'pink', 'lime', 'gray'];
const PLAN_RING_HEX: Record<string, string> = {
  blue: '#339af0',
  teal: '#20c997',
  orange: '#fd7e14',
  grape: '#cc5de8',
  cyan: '#22b8cf',
  pink: '#f06595',
  lime: '#94d82d',
  gray: '#868e96',
};

const toNumber = (v: number | string | undefined | null): number => {
  if (v === null || v === undefined) return 0;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : 0;
};

const FinancesPage: React.FC = () => {
  const { colorScheme } = useMantineColorScheme();
  const queryClient = useQueryClient();
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 11);
    d.setDate(1);
    return d;
  });
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  const startISO = startDate ? startDate.toISOString().replace('Z', '') : undefined;
  const endISO = endDate ? endDate.toISOString().replace('Z', '') : undefined;

  const statsResult = useQuery(adminFinanceStatsQuery(startISO, endISO));
  const paymentsResult = useQuery(adminPaymentsBetweenQuery(startISO, endISO));
  const userStatsResult = useQuery(adminUserStatisticsQuery());

  const stats = statsResult.data as FinanceStats | undefined;
  const payments = (paymentsResult.data as PaymentHistoryDTO[] | undefined) ?? [];
  const userStats = userStatsResult.data as UserStatistics | undefined;
  const statsLoading = statsResult.isFetching;
  const paymentsLoading = paymentsResult.isFetching;
  const userStatsLoading = userStatsResult.isFetching;
  const loading = statsLoading || paymentsLoading || userStatsLoading;
  const activeSubscriptions = userStats
    ? Object.entries(userStats.distribucionPorPlan)
        .filter(([name]) => {
          const lower = name.toLowerCase();
          return lower !== 'sin plan' && lower !== 'free';
        })
        .reduce((acc, [, count]) => acc + count, 0)
    : 0;

  const [statusFilter, setStatusFilter] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPayment, setSelectedPayment] = useState<PaymentHistoryDTO | null>(null);
  const itemsPerPage = 10;

  const dark = colorScheme === 'dark';
  // Paleta sobria unificada con Reactivos/Dashboard.
  const cardStyle: React.CSSProperties = {
    backgroundColor: dark ? 'rgba(30, 41, 59, 0.7)' : 'rgba(247, 243, 238, 0.95)',
    border: `1px solid ${dark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(221, 216, 209, 0.8)'}`,
    boxShadow: 'none',
  };

  const textPrimary = dark ? '#e2e8f0' : '#2d2a26';
  const textMuted = dark ? '#94a3b8' : '#8c8478';
  const colActiva = dark ? '#86b8a6' : '#4f8a74';
  const primaryBtn = { root: { backgroundColor: textPrimary, color: dark ? '#1e293b' : '#f7f3ee' } };
  const outlineBtn = { root: { borderColor: textPrimary, color: textPrimary, backgroundColor: 'transparent' } };

  const formatCurrency = useCallback((amount: number | string) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(toNumber(amount));
  }, []);

  const formatMonth = useCallback((monthStr: string) => {
    const [year, month] = monthStr.split('-');
    const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    return `${months[parseInt(month) - 1]} ${year.slice(2)}`;
  }, []);

  const formatDate = useCallback((dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  }, []);

  const handleRefresh = useCallback(() => {
    if (!startISO || !endISO) return;
    queryClient.invalidateQueries({ queryKey: ['adminFinanceStats', startISO, endISO] });
    queryClient.invalidateQueries({ queryKey: ['adminPaymentsBetween', startISO, endISO] });
    queryClient.invalidateQueries({ queryKey: ['adminUserStatistics'] });
    setCurrentPage(1);
  }, [queryClient, startISO, endISO]);

  const getStatusBadge = (status: string) => {
    const map: Record<string, { color: string; label: string }> = {
      COMPLETED: { color: dark ? '#86b8a6' : '#4f8a74', label: 'Completado' },
      PENDING: { color: dark ? '#cdb487' : '#a07d3c', label: 'Pendiente' },
      FAILED: { color: dark ? '#d29ba0' : '#b5565e', label: 'Fallido' },
      REFUNDED: { color: dark ? '#94a3b8' : '#8c8478', label: 'Reembolsado' },
    };
    const cfg = map[status] || { color: dark ? '#94a3b8' : '#8c8478', label: status };
    return <Text size="xs" fw={600} style={{ color: cfg.color }}>{cfg.label}</Text>;
  };

  const visiblePayments = useMemo(
    () => payments.filter((p) => p.status === 'COMPLETED' || p.status === 'REFUNDED'),
    [payments]
  );

  const filteredPayments = useMemo(() => {
    if (!statusFilter) return visiblePayments;
    return visiblePayments.filter((p) => p.status === statusFilter);
  }, [visiblePayments, statusFilter]);

  const paginatedPayments = filteredPayments.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const monthlyBreakdownChart = useMemo(() => {
    if (!stats) return [];
    return stats.monthlyBreakdown.map((item) => ({
      month: formatMonth(item.month),
      Ingresos: toNumber(item.revenue),
    }));
  }, [stats, formatMonth]);

  const planSections = useMemo(() => {
    if (!stats) return [];
    const visible = stats.revenueByPlan.filter(
      (r) => r.plan.toLowerCase() !== 'plan mensual'
    );
    const total = visible.reduce((acc, r) => acc + toNumber(r.revenue), 0);
    return visible.map((row, i) => {
      const value = toNumber(row.revenue);
      const pct = total > 0 ? (value / total) * 100 : 0;
      const color = PLAN_RING_COLORS[i % PLAN_RING_COLORS.length];
      return { plan: row.plan, revenue: value, pct, color };
    });
  }, [stats]);

  const deltaPercent = toNumber(stats?.deltaPercent);
  const deltaPositive = deltaPercent >= 0;

  const handleDownloadCsv = async () => {
    if (!startDate || !endDate) return;
    try {
      const startISO = startDate.toISOString().replace('Z', '');
      const endISO = endDate.toISOString().replace('Z', '');
      const blob = await paymentHistoryService.downloadCsv(startISO, endISO, statusFilter || undefined);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const startStr = startDate.toISOString().slice(0, 10);
      const endStr = endDate.toISOString().slice(0, 10);
      link.download = `pagos_${startStr}_${endStr}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error descargando CSV:', e);
    }
  };

  return (
    <PageTransition type="medical" duration={800}>
      <Box style={{ flex: 1, padding: '16px 24px', overflowY: 'auto', position: 'relative' }}>
        <LoadingOverlay visible={loading} zIndex={1000} overlayProps={{ blur: 2 }} />

        <Stack gap="lg">
          <AdminPageHeader title="Finanzas" subtitle="Ingresos, pagos y métricas financieras" />

          {/* Filtros */}
          <Card withBorder radius="lg" p="md" style={cardStyle}>
            <Group>
              <DateInput
                label="Fecha inicio"
                placeholder="Seleccionar"
                value={startDate}
                onChange={setStartDate}
                leftSection={<IconCalendar size={16} />}
                style={{ flex: 1 }}
              />
              <DateInput
                label="Fecha fin"
                placeholder="Seleccionar"
                value={endDate}
                onChange={setEndDate}
                leftSection={<IconCalendar size={16} />}
                style={{ flex: 1 }}
              />
              <Button
                styles={primaryBtn}
                leftSection={<IconSearch size={16} />}
                onClick={handleRefresh}
                style={{ alignSelf: 'flex-end' }}
              >
                Buscar
              </Button>
              <Button
                variant="default"
                styles={outlineBtn}
                leftSection={<IconDownload size={16} />}
                onClick={handleDownloadCsv}
                style={{ alignSelf: 'flex-end' }}
              >
                Descargar CSV
              </Button>
            </Group>
          </Card>

          {/* KPIs principales */}
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            <Card withBorder radius="lg" p="lg" style={cardStyle}>
              <Group wrap="nowrap">
                <IconCash size={22} color={textPrimary} style={{ flexShrink: 0 }} />
                <div>
                  <Text size="xl" fw={700} style={{ color: textPrimary }}>
                    {formatCurrency(stats?.totalRevenue ?? 0)}
                  </Text>
                  <Text size="xs" fw={500} style={{ color: textMuted }}>
                    Ingresos del Rango
                  </Text>
                </div>
              </Group>
            </Card>

            <Card withBorder radius="lg" p="lg" style={cardStyle}>
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Group wrap="nowrap">
                  <IconCash size={22} color={textPrimary} style={{ flexShrink: 0 }} />
                  <div>
                    <Text size="xl" fw={700} style={{ color: textPrimary }}>
                      {formatCurrency(stats?.monthlyRevenue ?? 0)}
                    </Text>
                    <Text size="xs" fw={500} style={{ color: textMuted }}>
                      Mes Actual
                    </Text>
                  </div>
                </Group>
                {stats && (
                  <Group gap={3} wrap="nowrap" align="center">
                    {deltaPositive ? <IconTrendingUp size={13} color={colActiva} /> : <IconTrendingDown size={13} color={dark ? '#d29ba0' : '#b5565e'} />}
                    <Text size="xs" fw={700} style={{ color: deltaPositive ? colActiva : (dark ? '#d29ba0' : '#b5565e') }}>
                      {deltaPositive ? '+' : ''}{deltaPercent.toFixed(1)}%
                    </Text>
                  </Group>
                )}
              </Group>
              {stats && (
                <Text size="xs" mt={4} style={{ color: textMuted }}>
                  vs. mes anterior: {formatCurrency(stats.lastMonthRevenue)}
                </Text>
              )}
            </Card>

            <Card withBorder radius="lg" p="lg" style={cardStyle}>
              <Group wrap="nowrap">
                <IconReportMoney size={22} color={textPrimary} style={{ flexShrink: 0 }} />
                <div>
                  <Text size="xl" fw={700} style={{ color: textPrimary }}>
                    {formatCurrency(stats?.mrr ?? 0)}
                  </Text>
                  <Text size="xs" fw={500} style={{ color: textMuted }}>
                    Ingreso recurrente mensual
                  </Text>
                </div>
              </Group>
            </Card>

            <Card withBorder radius="lg" p="lg" style={cardStyle}>
              <Group wrap="nowrap">
                <IconUsers size={22} color={textPrimary} style={{ flexShrink: 0 }} />
                <div>
                  <CountUpNumber
                    value={activeSubscriptions}
                    duration={1500}
                    style={{ color: textPrimary, fontSize: '1.4rem', fontWeight: 700 }}
                  />
                  <Text size="xs" fw={500} style={{ color: textMuted }}>
                    Suscripciones de pago activas
                  </Text>
                </div>
              </Group>
            </Card>
          </SimpleGrid>

          {/* Gráfico de barras + distribución por plan */}
          <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="lg">
            <Card withBorder radius="lg" p="xl" style={cardStyle}>
              <Title order={5} mb="md" style={{ color: textPrimary }}>
                Ingresos Mensuales
              </Title>
              {monthlyBreakdownChart.length > 0 ? (
                <BarChart
                  h={300}
                  data={monthlyBreakdownChart}
                  dataKey="month"
                  series={[{ name: 'Ingresos', color: 'blue.6' }]}
                  tickLine="y"
                  gridAxis="y"
                  withTooltip
                  tooltipAnimationDuration={200}
                  valueFormatter={(value) => formatCurrency(value)}
                />
              ) : (
                <Text ta="center" c="dimmed" py="xl">
                  No hay datos para el rango seleccionado
                </Text>
              )}
            </Card>

            <Card withBorder radius="lg" p="xl" style={cardStyle}>
              <Title order={5} mb="md" style={{ color: textPrimary }}>
                Ingresos por Plan
              </Title>
              {planSections.length === 0 ? (
                <Text ta="center" c="dimmed" py="xl">
                  No hay ingresos para el rango
                </Text>
              ) : (
                <>
                  <Group justify="center" mb="lg">
                    <RingProgress
                      size={160}
                      thickness={16}
                      sections={planSections.map((s) => ({
                        value: s.pct,
                        color: s.color,
                        tooltip: `${s.plan} – ${formatCurrency(s.revenue)}`,
                      }))}
                      label={
                        <Text ta="center" fw={700} size="md" style={{ color: textPrimary }}>
                          Ingresos
                        </Text>
                      }
                    />
                  </Group>
                  <Stack gap="xs">
                    {planSections.map((s) => (
                      <Group key={s.plan} justify="space-between">
                        <Group gap="xs">
                          <Box w={10} h={10} style={{ backgroundColor: PLAN_RING_HEX[s.color], borderRadius: '50%' }} />
                          <Text size="xs" style={{ color: textMuted }}>{s.plan}</Text>
                        </Group>
                        <Text size="xs" fw={600} style={{ color: textPrimary }}>
                          {formatCurrency(s.revenue)} ({s.pct.toFixed(1)}%)
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                </>
              )}
            </Card>
          </SimpleGrid>

          {/* Tabla de pagos */}
          <Card withBorder radius="lg" p="xl" style={cardStyle}>
            <Group justify="space-between" mb="md" wrap="wrap">
              <Title order={5} style={{ color: textPrimary }}>
                Pagos del Rango ({filteredPayments.length})
              </Title>
              <Select
                value={statusFilter}
                onChange={(v) => { setStatusFilter(v || ''); setCurrentPage(1); }}
                data={STATUS_OPTIONS}
                style={{ width: 200 }}
                placeholder="Filtrar por estado"
              />
            </Group>

            <Table.ScrollContainer minWidth={600}>
              <Table highlightOnHover verticalSpacing="sm" withRowBorders style={{ textAlign: 'left' }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ color: textMuted, fontWeight: 600, fontSize: 12 }}>Usuario</Table.Th>
                    <Table.Th style={{ color: textMuted, fontWeight: 600, fontSize: 12 }}>Plan</Table.Th>
                    <Table.Th style={{ color: textMuted, fontWeight: 600, fontSize: 12 }}>Monto</Table.Th>
                    <Table.Th style={{ color: textMuted, fontWeight: 600, fontSize: 12 }}>Fecha</Table.Th>
                    <Table.Th style={{ color: textMuted, fontWeight: 600, fontSize: 12 }}>Estado</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {paginatedPayments.map((payment) => (
                    <Table.Tr
                      key={payment.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedPayment(payment)}
                    >
                      <Table.Td style={{ color: textPrimary }}>{payment.userEmail || 'N/A'}</Table.Td>
                      <Table.Td style={{ color: textMuted }}>{payment.planName || 'N/A'}</Table.Td>
                      <Table.Td fw={600} style={{ color: textPrimary }}>{formatCurrency(payment.amount)}</Table.Td>
                      <Table.Td style={{ color: textMuted }}>{formatDate(payment.paymentDate)}</Table.Td>
                      <Table.Td>{getStatusBadge(payment.status)}</Table.Td>
                    </Table.Tr>
                  ))}
                  {paginatedPayments.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={5}>
                        <Text ta="center" c="dimmed" py="md">No se encontraron pagos</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            {filteredPayments.length > itemsPerPage && (
              <Group justify="center" mt="md">
                <Pagination
                  total={Math.ceil(filteredPayments.length / itemsPerPage)}
                  value={currentPage}
                  onChange={setCurrentPage}
                  color="gray"
                  size="sm"
                />
              </Group>
            )}
          </Card>
        </Stack>

        {/* Modal detalle */}
        <Modal
          opened={!!selectedPayment}
          onClose={() => setSelectedPayment(null)}
          title={selectedPayment ? `Pago #${selectedPayment.id}` : ''}
          size="lg"
          centered
        >
          {selectedPayment && (
            <Stack gap="sm">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Usuario</Text>
                <Text fw={500}>{selectedPayment.userEmail || 'N/A'}</Text>
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Plan</Text>
                <Text fw={500}>{selectedPayment.planName || 'N/A'}</Text>
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Monto</Text>
                <Text fw={700}>{formatCurrency(selectedPayment.amount)} {selectedPayment.currency}</Text>
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Fecha</Text>
                <Text fw={500}>{formatDate(selectedPayment.paymentDate)}</Text>
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Estado</Text>
                {getStatusBadge(selectedPayment.status)}
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Método</Text>
                <Text fw={500}>{selectedPayment.paymentMethod || 'N/A'}</Text>
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text size="sm" c="dimmed">Referencia</Text>
                <Text fw={500} size="xs" style={{ fontFamily: 'monospace' }}>
                  {selectedPayment.paymentReference || 'N/A'}
                </Text>
              </Group>
              {selectedPayment.description && (
                <>
                  <Divider />
                  <Stack gap={4}>
                    <Text size="sm" c="dimmed">Descripción</Text>
                    <Text>{selectedPayment.description}</Text>
                  </Stack>
                </>
              )}
              {(selectedPayment as any).metadata && (
                <>
                  <Divider />
                  <Stack gap={4}>
                    <Text size="sm" c="dimmed">Metadata</Text>
                    <ScrollArea h={150}>
                      <Code block>{JSON.stringify((selectedPayment as any).metadata, null, 2)}</Code>
                    </ScrollArea>
                  </Stack>
                </>
              )}
            </Stack>
          )}
        </Modal>
      </Box>
    </PageTransition>
  );
};

export default FinancesPage;
